import { Intent } from "@blueprintjs/core";

import ServiceRegistry from "core/util/ServiceRegistry";
import { iDef } from "core/util/TypeUtils";
import { OrganizationUserEnrollment } from "panels/bundleEntries/entities/OrganizationUserEnrollment";
import { ForumToaster } from "panels/forum/forumActions";

type ID = number | string | any;

export enum Completion {
	NotStarted = "NotStarted",
	Started = "Started",
	Incomplete = "Incomplete",
	Completed = "Completed",
	Passed = "Passed",
	Failed = "Failed"
}

export enum Status {
	Active = "Active",
	Inactive = "Inactive",
	Expired = "Expired",
	Deleted =  "Deleted",
	Withdrawn = "Withdrawn",
	Candidate = "Candidate",
	Finished = "Finished",
	Suspended = "Suspended"
}

export interface ILaunchURL {
	viewURL?: string;
	launchURL?: string;
	cartURL?: string;
}

export interface ICertificate {
	id: ID;
	name: string;
}

export interface IIssuedCertificate {
	id: ID;
	guid: string;
	certificate?: ICertificate;
}

export interface ISubscription {
	id: string;
	entry?: IEntry;
	product?: IProduct;
	completion?: Completion;
	launchURL?: ILaunchURL;
	startDate: string;
	endDate: string;
	completedOn?: string;
	timeSpent?: number;
	score?: number;
	maxScore?: number;
	lastVistied?: string;
	tags?: ISubscriptionTag[];
	certificates?: IIssuedCertificate[];
}

export interface IProduct {
	id: string;
	entry?: IEntry;
	name?: string;
	subTitle?: string;
	displayName?:string;
	description?: string;
	iconURL?: string;
	smallIconURL?: string;
	tags?: ITag[];
	subCredits?: ICredit[];
	displayStartDate?: string;
	displayEndDate?: string;
}

export interface IEntry {
	id: number | string;
	displayName?: string;
	subscription?: ISubscription;
	product?: IProduct;
	launchURL?: ILaunchURL;
	groupDiscount?: IGroupDiscount;
	variants?:IEntryVariants[];
}

export interface ITag {
	id: ID;
	name: string;
}

export interface ISubscriptionTag {
	id: ID;
	type: string;
	tag: ITag;
}

export interface ICreditType {
	id: ID;
	name: string;
}

export interface ICredit {
	id: ID;
	creditType: ICreditType;
	value: number;
}

export interface ICourseWrapperArgs {
	entry?: IEntry;
	subscription?: ISubscription;
	product?: IProduct;
}

export interface IGroupDiscount {
	data: IDiscountData[];
}

export interface IDiscountData {
	min: number;
	max: number;
	cost: number;
}

export interface IEntryVariants {
	id: string;
    price: number;
    currency: string;	
}

export class CourseWrapper {
	public static forEntry(entry: IEntry): CourseWrapper {
		if (!entry || !entry.product) {
			return undefined;
		}
		return new CourseWrapper(entry.product, entry, entry.subscription, null);
	}

	public static forBundleEntry(entry: IEntry, bundleIds: ID[]): CourseWrapper {
		if (!entry || !entry.product) {
			return undefined;
		}
		return new CourseWrapper(entry.product, entry, entry.subscription, bundleIds);
	}

	public static forSubscription(subscription: ISubscription): CourseWrapper {
		if (!subscription || !subscription.entry || !subscription.product) {
			return undefined;
		}
		return new CourseWrapper(subscription.product || subscription.entry.product, subscription.entry, subscription, null);
	}

	private subscription?: ISubscription; // might have this
	private product: IProduct; // MUST have this
	private entry: IEntry; // MUST have this
	private bundleIds?: ID[];

	constructor(product: IProduct, entry: IEntry, subscription?: ISubscription, bundleIds?: ID[]) {
		this.subscription = subscription;
		this.entry = entry || {} as IEntry;
		this.product = product || {} as IProduct;
		this.bundleIds = bundleIds || {} as ID;
	}




	public getEntryId(): ID { return this.entry.id; }

	public getItemId(): ID { return this.product ? this.product.id : undefined; }

	public getTitle(): string { return this.entry.displayName || this.product.displayName || this.product.name; }

	public getSubtitle(): string { return this.product.subTitle || undefined; }

	public getDescription(): string { return this.product.description || undefined; }

	public getIconUrl(): string { return this.product.iconURL; }

	public getSmallIconUrl(): string { return this.product.smallIconURL; }

	public getTags(): ITag[] { return this.product.tags || []; }

	public getSubscriptionTags(): ISubscriptionTag[] { return this.subscription ? this.subscription.tags || [] : []; }

	public getCredits(): ICredit[] { return this.product.subCredits || []; }

	public getDisplayStartDate(): Date {
		return this.product && this.product.displayStartDate && new Date(this.product.displayStartDate);
	}

	public getDisplayEndDate(): Date {
		return this.product && this.product.displayEndDate && new Date(this.product.displayEndDate);
	}

	public isEnrolled(): boolean {
		return this.subscription !== undefined;
	}

	public getSubscription(): ISubscription { return this.subscription; }
	public hasSubscription(): boolean { return (this.getSubscription() !== null && this.getSubscription() !== undefined); }

	public getSubscriptionStart(): string {
		return this.subscription ? this.subscription.startDate : undefined;
	}

	public getSubscriptionEnd(): string {
		return this.subscription ? this.subscription.endDate : undefined;
	}

	public isSubscriptionExpired(): boolean {
		if(this.getSubscriptionEnd()){
			const now = Date.now();
			const end = Date.parse(this.getSubscriptionEnd());
			return now > end;
		}else{
			return false;
		}
	}

	public hasActiveSubscription(): boolean {
		if (!this.isEnrolled() || !this.getSubscriptionStart() || !this.getSubscriptionEnd()) {
			return false;
		}
		const now = Date.now();
		const start = Date.parse(this.getSubscriptionStart());
		const end = Date.parse(this.getSubscriptionEnd());

		return now > start && now < end;
	}

	public getCompletion(): Completion | undefined {
		return this.subscription ? this.subscription.completion : undefined;
	}

	public getProgressPercent(): number {
		switch(this.getCompletion()) {
			case Completion.Completed:
				return 1;
			case Completion.Passed:
				return 1;
			case Completion.Incomplete:
				return 0.5;
			case Completion.Started:
				return 0.5;
			default:
				return 0;
		}
	}

	public getTimeSpent(): number {
		return this.getSubscription() ? (this.getSubscription().timeSpent || 0) : 0;
	}

	public getLastVisited(): string | undefined {
		return this.subscription ? this.subscription.lastVistied : undefined;
	}

	public getCompletionDate(): string | undefined {
		return this.subscription ? this.subscription.completedOn : undefined;
	}

	public isCompleted(): boolean {
		return (this.getCompletion() === Completion.Completed || this.getCompletion() === Completion.Passed);
	}

	public getScore(): number { return this.subscription ? this.subscription.score : undefined; }
	public getMaxScore(): number { return this.subscription ? this.subscription.maxScore : undefined; }
	public getScorePercent(): number | undefined {
		return (this.getScore() || 0) / 100;
	}

	/**
	 * get subscription tags marked as "Strengths"
	 * @param tagIds - (optional) filter this list to certain tag ids
	 */
	public getStrengths(tagIds?: ID[]): ISubscriptionTag[] {
		const strengths = this.getSubscriptionTags().filter((st) => st.type === 'STRONG');
		return (strengths.length > 0 && iDef(tagIds)) ? strengths.filter((st) => tagIds.indexOf(st.tag.id) > -1) : strengths;
	}

	/**
	 * get subscription tags marked as "Weaknesses"
	 * @param tagIds - (optional) filter this list to certain tag ids
	 */
	public getWeaknesses(tagIds?: ID[]): ISubscriptionTag[] {
		const weaknesses = this.getSubscriptionTags().filter((st) => st.type === 'WEAK');
		return (weaknesses.length > 0 && iDef(tagIds)) ? weaknesses.filter((st) => tagIds.indexOf(st.tag.id) > -1) : weaknesses;
	}

	public isLaunchable(): boolean {
		return this.getLaunchUrl() !== undefined;
	}

	public isBuyable(): boolean {
		return this.getCartUrl() !== undefined;
	}

	public isViewable(): boolean {
		return true; // might always be true?
	}

	public getLaunchUrl(): string {
		return (this.subscription && this.subscription.launchURL && this.subscription.launchURL.launchURL)
			|| (this.entry && this.entry.launchURL && this.entry.launchURL.launchURL)
			|| undefined;
	}

	public getViewUrl(): string {
		return (this.subscription.launchURL && this.subscription.launchURL.viewURL)
			|| (this.entry.launchURL && this.entry.launchURL.viewURL)
			|| undefined;
	}

	public getCertificates(): IIssuedCertificate[] {
		return this.subscription && this.subscription.certificates || [];
	}

	public launchCourse(): void {
		this.launch();
	}

	public viewCourse(): void {
		window.open(this.getViewUrl(), "_blank");
	}

	public getCartUrl(): string {
		return (this.subscription && this.subscription.launchURL && this.subscription.launchURL.cartURL)
			|| (this.entry && this.entry.launchURL && this.entry.launchURL.cartURL)
			|| undefined;
	}

	public getGroupDiscount(): IDiscountData[] {
		return (this.entry.groupDiscount && this.entry.groupDiscount.data) || [];
	}

	public getEntry(): IEntry {
		return this.entry;
	}

	public updateEnrollCount(): void {
		const organizationUserEnrollment = {bundleIds: this.bundleIds, enrollCountUpdate: true} as any;
		ServiceRegistry.getMutationService().update(organizationUserEnrollment, OrganizationUserEnrollment).then((res) => {
			this.handleLaunch(res);
		}).catch((err) => {
			console.log(err);
		});
	}

	private handleLaunch(res: any): void {
		if(res.entity != null && res.entity !== undefined) {
			this.launch();
		} else {
		ForumToaster.show( {
				intent: Intent.DANGER,
				message: "Enrollment limit exceeded for the organization."
			});
		}
	}

	private launch(): void {
		window.open(this.getLaunchUrl(), "_blank");
	}

}



// WEBPACK FOOTER //
// ./src/common/entities/courseWrapper/CourseWrapper.ts